/*
 * Header sidebar multi-level drill-down.
 *
 * The vendor `HeaderSidebar` class wires L1 → L2 via aria-controls on .header-sidebar__main-panel
 * buttons and a single shadow-DOM back button that pops the collapsible-panel straight back to main.
 *
 * This file extends that to support an extra L2 → L3 drill:
 *   1. Buttons with [data-drill][aria-controls] inside .header-sidebar__collapsible-panel set the
 *      collapsible-panel's aria-activedescendant to the target panel ID — reusing the existing
 *      switchPanel animation between any two sibling sub-panels.
 *   2. The shadow-DOM back button (data-action="close-panel") is intercepted in the capture phase:
 *      if the currently-active sub-panel has [data-parent], we walk up one level instead of
 *      letting the vendor handler close the whole collapsible-panel.
 */

const initHeaderSidebarMultilevel = () => {
  const sidebar = document.getElementById("sidebar-menu");
  if (!sidebar) return;

  const collapsiblePanel = sidebar.querySelector("header-sidebar-collapsible-panel");
  if (!collapsiblePanel) return;

  collapsiblePanel.addEventListener("click", (clickEvent) => {
    const drillButton = clickEvent.target.closest("[data-drill][aria-controls]");
    if (!drillButton || !collapsiblePanel.contains(drillButton)) return;
    const targetPanelId = drillButton.getAttribute("aria-controls");
    if (!targetPanelId || !collapsiblePanel.querySelector(`#${CSS.escape(targetPanelId)}`)) return;
    collapsiblePanel.setAttribute("aria-activedescendant", targetPanelId);
  });

  const wireBackButtonInterceptor = () => {
    const backButton = sidebar.shadowRoot?.querySelector('[data-action="close-panel"]');
    if (!backButton) return false;

    backButton.addEventListener(
      "click",
      (clickEvent) => {
        const activeId = collapsiblePanel.getAttribute("aria-activedescendant");
        if (!activeId) return;
        const activePanel = collapsiblePanel.querySelector(`#${CSS.escape(activeId)}`);
        const parentId = activePanel?.dataset.parent;
        if (!parentId) return;
        clickEvent.stopImmediatePropagation();
        collapsiblePanel.setAttribute("aria-activedescendant", parentId);
      },
      { capture: true }
    );
    return true;
  };

  if (wireBackButtonInterceptor()) return;

  const shadowReadyObserver = new MutationObserver(() => {
    if (wireBackButtonInterceptor()) shadowReadyObserver.disconnect();
  });
  shadowReadyObserver.observe(sidebar, { childList: true, subtree: true });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderSidebarMultilevel);
} else {
  initHeaderSidebarMultilevel();
}

document.addEventListener("shopify:section:load", initHeaderSidebarMultilevel);
